import { px } from '@adsk/alloy-react-helpers';
import alloyTheme from '@adsk/alloy-react-theme';

import { AlloyLegacyThemeDesignTokens, PageHeaderDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = WeaveTheme['tokens'];

export const getPageHeaderTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): PageHeaderDesignTokens => ({
  container: {
    _: {
      display: 'flex',
      'flex-flow': 'row nowrap',
      width: '100%',
      color: theme.tokens.colors.text.primary.value,
    },
    withBorder: {
      'border-bottom': `1px solid ${theme.tokens.colors.border.subtle.value}`,
    },
  },
  contentColumn: {
    _: {
      display: 'flex',
      'flex-flow': 'column',
      'flex-grow': 1,
      transition: theme.transitions.create(['padding']),
    },
    collapsed: {
      false: { 'padding-top': px(theme.spacing.M) },
      true: { 'padding-top': px(theme.spacing.XS) },
    },
  },
  rightColumn: {
    display: 'flex',
    'flex-flow': 'column',
    'justify-content': 'flex-end',
    'margin-left': '40px',
    'margin-bottom': px(theme.spacing.XXS),
  },
  title: {
    _: {
      transition: theme.transitions.create(['font-size', 'padding']),
    },
    collapsed: {
      false: {
        'font-family': theme.typography.title.fontFamily,
        'font-weight': String(theme.typography.title.fontWeight),
        'font-size': theme.typography.title.fontSize,
        'line-height': theme.typography.title.lineHeight,
        'padding-bottom': px(theme.spacing.M),
      },
      true: {
        'font-family': theme.typography.bodyLarge.fontFamily,
        'font-weight': String(theme.typography.bodyLarge.fontWeight),
        'font-size': theme.typography.bodyLarge.fontSize,
        'line-height': theme.typography.bodyLarge.lineHeight,
        'padding-bottom': px(theme.spacing.XS),
      },
    },
    subtitle: {
      true: { 'padding-bottom': '0' },
      false: {},
    },
  },
  subtitle: {
    _: {
      color: theme.tokens.colors.text.secondary.value,
      transition: theme.transitions.create(['font-size', 'padding']),
    },
    collapsed: {
      false: {
        'font-family': theme.typography.heading2.fontFamily,
        'font-weight': String(theme.typography.heading2.fontWeight),
        'font-size': theme.typography.heading2.fontSize,
        'line-height': theme.typography.heading2.lineHeight,
        'padding-bottom': px(theme.spacing.M),
      },
      true: {
        'font-family': theme.typography.bodyMedium.fontFamily,
        'font-weight': String(theme.typography.bodyMedium.fontWeight),
        'font-size': theme.typography.bodyMedium.fontSize,
        'line-height': theme.typography.bodyMedium.lineHeight,
        'padding-bottom': px(theme.spacing.XS),
      },
    },
  },
});

export const getPageHeaderTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): PageHeaderDesignTokens => {
  const semantic = tokens?.semantic;
  const generic = tokens?.generic;
  const fontSize = generic?.['font-size'];
  const lineHeight = generic?.['line-height'];

  return {
    container: {
      _: {
        display: 'flex',
        'flex-flow': 'row nowrap',
        width: '100%',
        color: semantic?.['text-color']?.medium?.default,
      },
      withBorder: {
        'border-bottom': `1px solid ${semantic?.['border-color']?.divider?.light}`,
      },
    },
    contentColumn: {
      _: {
        display: 'flex',
        'flex-flow': 'column',
        'flex-grow': 1,
        transition: alloyTheme.transitions.create(['padding']),
      },
      collapsed: {
        false: { 'padding-top': generic?.spacing?.[16] },
        true: { 'padding-top': generic?.spacing?.[8] },
      },
    },
    rightColumn: {
      display: 'flex',
      'flex-flow': 'column',
      'justify-content': 'flex-end',
      'margin-left': '40px',
      'margin-bottom': generic?.spacing?.[4],
    },
    title: {
      _: {
        'font-family': fontFamily,
        transition: alloyTheme.transitions.create(['font-size', 'padding']),
      },
      collapsed: {
        false: {
          'font-family': fontFamily,
          'font-weight': '400',
          'font-size': fontSize?.[30],
          'line-height': '42px',
          'padding-bottom': generic?.spacing?.[16],
        },
        true: {
          'font-family': fontFamily,
          'font-weight': '500',
          'font-size': fontSize?.[16],
          'line-height': lineHeight?.[22],
          'padding-bottom': generic?.spacing?.[8],
        },
      },
      subtitle: {
        true: { 'padding-bottom': '0' },
        false: {},
      },
    },
    subtitle: {
      _: {
        color: semantic?.['text-color']?.light,
        transition: alloyTheme.transitions.create(['font-size', 'padding']),
      },
      collapsed: {
        false: {
          'font-family': fontFamily,
          'font-weight': '400',
          'font-size': fontSize?.[20],
          'line-height': lineHeight?.[26],
          'padding-bottom': generic?.spacing?.[16],
        },
        true: {
          'font-family': fontFamily,
          'font-weight': '500',
          'font-size': fontSize?.[14],
          'line-height': lineHeight?.[20],
          'padding-bottom': generic?.spacing?.[8],
        },
      },
    },
  };
};
